import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    HostListener,
    Input,
    OnChanges,
    OnInit,
    Output,
    QueryList,
    Renderer2,
    SimpleChanges,
    ViewChild,
    ViewChildren,
} from "@angular/core";
import { SpsActionDescriptor, SpsOptionListOption } from "@spscommerce/ds-shared";
import { AttributePresenceInput, observablize } from "@spscommerce/ng-utils";
import { Position, PositionAnchor, PositioningService } from "@spscommerce/positioning";
import { debounce, Eventually, onNextTick } from "@spscommerce/utils";
import autobind from "autobind-decorator";
import * as isEmptyImport from "lodash.isempty";
import { Observable } from "rxjs";
import * as scrollIntoViewImport from "scroll-into-view-if-needed";

// Some imports need this to work in all contexts incl ng-packagr/rollup
const isEmpty = (<any>isEmptyImport).default || isEmptyImport;
const scrollIntoView = (<any>scrollIntoViewImport).default || scrollIntoViewImport;

@Component({
    selector: "sps-option-list",
    templateUrl: "./sps-option-list.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SpsOptionListComponent<T> implements OnChanges, OnInit {
    private static _idNum = 0;
    private _positioningService = new PositioningService();

    @HostBinding("attr.id") @Input() id: string;
    @Input() options:
        | Eventually<Iterable<T>>
        | Observable<Iterable<T>>
        | ((filter?: string) => Eventually<Iterable<T>> | Observable<Iterable<T>>);
    @Input() textKey: string;
    @Input() captionKey: string;
    @Input() zeroState: string;
    @Input() @AttributePresenceInput() hideInlineSearch: boolean;
    @Input() searchPlaceholder = "Search…";
    @Input() selectedOption: T;
    @Input() attachTo: HTMLElement;
    @Input() @AttributePresenceInput() conformWidth: boolean;
    @Input() @AttributePresenceInput() alignLeft: boolean;
    @Input() searchDebounce: number;
    @Input() role: string;
    @Input() offsets: [number, number] = [0, 0];
    @Input() zIndex: number;
    @HostBinding("attr.aria-labelledby") @Input() ariaLabelledBy: string;

    _nullOptionOption: SpsOptionListOption<null>;
    @Input() nullOption: string;

    _specialActionOption: SpsOptionListOption<SpsActionDescriptor>;
    @Input() specialAction: SpsActionDescriptor;

    private _search = "";
    _searchReplacementPattern: RegExp;
    @Input()
    get search() {
        return this._search;
    }
    set search(newValue: string) {
        this._search = newValue;
        this.searchChange.emit(newValue);
        this._updateOptions();
    }
    @Output() searchChange = new EventEmitter<string>();

    @Output() optionSelected = new EventEmitter<T>();

    @HostBinding("class.sps-option-list") readonly spsOptionListClass = true;
    @HostBinding("class.z-stratum-dropdown") readonly zStratumDropdownClass = true;
    @HostBinding("attr.role") readonly roleAttr = "menu";

    private _isOpen = false;
    @HostBinding("class.sps-option-list--open")
    get isOpen() {
        return this._isOpen;
    }

    private _opensUpward = false;
    get opensUpward() {
        return this._opensUpward;
    }
    set opensUpward(newValue: boolean) {
        this._opensUpward = newValue;
        // Managing this class via @HostBinding is not working for some reason
        if (newValue) {
            this.renderer.addClass(this.elementRef.nativeElement, "sps-option-list--opens-upward");
        } else {
            this.renderer.removeClass(this.elementRef.nativeElement, "sps-option-list--opens-upward");
        }
    }

    @HostBinding("class.sps-option-list--searchable")
    isAsync = false;

    @HostBinding("attr.aria-activedescendent") get _ariaActiveDescendentAttr() {
        return this._highlightedOptionIndex > -1
            ? `${this.id}-option-${this._highlightedOptionIndex}`
            : null;
    }

    @HostBinding("class.sps-option-list--has-special-action")
    get hasVisibleSpecialAction() {
        return this.specialAction && !!this.specialAction.label;
    }

    @ViewChild("searchInput", { static: false }) searchInput: ElementRef;
    @ViewChildren("options") private _optionElements: QueryList<ElementRef>;

    private _ignoreIntersections = false;

    _optionList: ReadonlyArray<SpsOptionListOption<T>> = Object.freeze([]);
    _highlightedOptionIndex = -1;
    get highlightedOption() {
        return this._highlightedOptionIndex === this._optionList.length
            ? this._specialActionOption
            : this._optionList[this._highlightedOptionIndex];
    }

    get anyOptionHasIcon() {
        return this._optionList.some(option => option.value && !!option.value["icon"]);
    }

    get isEmpty() {
        return this._optionList.length === 0;
    }

    get pending() {
        return (
            this.options &&
            typeof this.options === "function" &&
            this.options["pending"]
        );
    }

    get _positioningOptions() {
        const position = this.opensUpward
            ? (this.alignLeft ? Position.TOP_LEFT : Position.TOP_RIGHT)
            : (this.alignLeft ? Position.BOTTOM_LEFT : Position.BOTTOM_RIGHT);
        const anchor = this.opensUpward ? PositionAnchor.BOTTOM_LEFT : PositionAnchor.TOP_LEFT;
        return {
            relativeTo: this.attachTo,
            useRelativeTargetWidth: this.conformWidth,
            position,
            anchor,
            offsets: this.offsets,
            zIndex: this.zIndex,
        };
    }

    constructor(
        private changeDetector: ChangeDetectorRef,
        private elementRef: ElementRef,
        private renderer: Renderer2,
    ) {
        this._positioningService.on("viewportIntersection", this._onViewportIntersection);
        this._positioningService.on("elementIntersection", this._onElementIntersection);
    }

    ngOnInit() {
        this.id = this.id || `sps-option-list-${SpsOptionListComponent._idNum++}`;
    }

    @autobind
    open() {
        if (!this._isOpen) {
            this._highlightedOptionIndex = -1;
            this._autoScrollIfNeeded();
            this._isOpen = true;
            this._positioningService.position(this.elementRef.nativeElement, this._positioningOptions);
            this.changeDetector.markForCheck();
        }
    }

    @autobind
    close() {
        this._highlightedOptionIndex = -1;
        this._isOpen = false;
        this.opensUpward = false;
        this._ignoreIntersections = false;
        this._positioningService.release(this.elementRef.nativeElement);
        this.changeDetector.markForCheck();
    }

    @autobind
    toggle() {
        if (this._isOpen) {
            this.close();
        } else {
            this.open();
        }
    }

    selectOption(option: SpsOptionListOption<T | SpsActionDescriptor> = this.highlightedOption) {
        if (!option.disabled) {
            if (typeof option.value === "function") {
                (option.value as any)();
            } else {
                this.optionSelected.emit((option as SpsOptionListOption<T>).value);
            }
            this.close();
        }
    }

    private _autoScrollIfNeeded() {
        if (this._highlightedOptionIndex > -1 && this._highlightedOptionIndex < this._optionList.length) {
            const element = this._optionElements.toArray()[this._highlightedOptionIndex]
                .nativeElement;
            scrollIntoView(element, {
                scrollMode: "if-needed",
                block: "nearest",
                inline: "nearest"
            });
        } else {
            this.elementRef.nativeElement.scrollTop = 0;
        }
    }

    @HostListener("mouseout")
    _resetHighlight() {
        this._highlightedOptionIndex = -1;
        this.changeDetector.markForCheck();
    }

    _onKeyDown(event: KeyboardEvent) {
        switch (event.key) {
            case "Tab":
            case "Escape":
                this.close();
                break;

            case "Enter":
                if (this.highlightedOption) {
                    event.preventDefault();
                    this.selectOption();
                }
                break;

            case "Up":
            case "ArrowUp": {
                let newHighlightIndex = this._highlightedOptionIndex;
                if (this.opensUpward) {
                    if (this._highlightedOptionIndex <= -1) {
                        // From no highlight, go up to the last option
                        newHighlightIndex = this._optionList.length - 1;
                    } else if (this._highlightedOptionIndex === 0) {
                        // From the first option, if there's a special action to go up to, go up to it
                        // Otherwise do not go up, we're at the top
                        if (this.specialAction) {
                                newHighlightIndex = this._optionList.length;
                        }
                    } else if (this._highlightedOptionIndex < this._optionList.length) {
                        // From an option lower than the 0th (index > 0) and not at the special action
                        // (index === length of option list), go up one
                        newHighlightIndex = this._highlightedOptionIndex - 1;
                    }
                } else if (this._highlightedOptionIndex > -1) {
                    // When opening downward (default) the very top is "no highlight", when index === -1,
                    // and indices get larger as you go downward. So just go up one
                    newHighlightIndex = this._highlightedOptionIndex - 1;
                }

                if (newHighlightIndex !== this._highlightedOptionIndex) {
                    event.preventDefault();
                    this._highlightedOptionIndex = newHighlightIndex;
                    this.changeDetector.markForCheck();
                    this._autoScrollIfNeeded();
                }
                break;
            }

            case "Down":
            case "ArrowDown": {
                this.open();

                let newHighlightIndex = this._highlightedOptionIndex;
                if (this.opensUpward) {
                    if (this._highlightedOptionIndex !== -1) {
                        if (this._highlightedOptionIndex >= this._optionList.length) {
                            // From the special action, go down to the first option
                            newHighlightIndex = 0;
                        } else if (this._highlightedOptionIndex === this._optionList.length - 1) {
                            // From the last option, go to "no highlight" (index === -1)
                            newHighlightIndex = -1;
                        } else {
                            // Otherwise, go down one
                            newHighlightIndex = this._highlightedOptionIndex + 1;
                        }
                    }
                } else if (this._highlightedOptionIndex < this._optionList.length - 1 + +!!this.specialAction) {
                    newHighlightIndex = this._highlightedOptionIndex + 1;
                }

                if (newHighlightIndex !== this._highlightedOptionIndex) {
                    event.preventDefault();
                    this._highlightedOptionIndex = newHighlightIndex;
                    this.changeDetector.markForCheck();
                    this._autoScrollIfNeeded();
                }
                break;
            }

            default:
                this.open();
                this._updateOptions();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        let updateOptions = false;

        if (changes.options) {
            updateOptions = true;
            if (typeof this.options === "function") {
                const options = this.options;
                this.options = debounce(function (filter?: string) {
                    const value = options(filter);
                    return value instanceof Observable ? value.toPromise() : value;
                }, this.searchDebounce);
                onNextTick(() => this.isAsync = true);
            }
        }

        if (changes.nullOption) {
            this._nullOptionOption = this.nullOption
                ? new SpsOptionListOption<null>(null, { text: this.nullOption })
                : null;
            updateOptions = true;
        }

        if (changes.specialAction) {
            this._specialActionOption = this.specialAction
                ? new SpsOptionListOption<SpsActionDescriptor>(this.specialAction, {
                    textKey: "label",
                    captionKey: "caption"
                })
                : null;
        }

        if (updateOptions) {
            this._updateOptions();
        }
    }

    @autobind
    private _onViewportIntersection(event) {
        if (
            !this._ignoreIntersections
            && event.target === this.elementRef.nativeElement
            && event.intersectionRatio < 1
        ) {
            const elementBounds = this.elementRef.nativeElement.getBoundingClientRect();
            this.opensUpward = elementBounds.bottom >= event.rootBounds.bottom
                && elementBounds.height < elementBounds.top;
            this._positioningService.reposition(this.elementRef.nativeElement, this._positioningOptions);
            this.changeDetector.markForCheck();
        }
    }

    @autobind
    private _onElementIntersection(event) {
        if (
            !this._ignoreIntersections
            && (
                event.target === this.elementRef.nativeElement
                || (event.intersectingWith && event.intersectingWith.indexOf(this.elementRef.nativeElement) > -1)
            )
        ) {
            this._ignoreIntersections = true;
            this.opensUpward = !this.opensUpward;
            this._positioningService.reposition(this.elementRef.nativeElement, this._positioningOptions);
            this.changeDetector.markForCheck();
        }
    }

    _onSearchClearClick(event: MouseEvent) {
        event.stopPropagation();
        this.search = "";
        this.searchInput.nativeElement.focus();
    }

    @autobind
    private _updateOptions() {
        const getOptions =
            typeof this.options === "function" ? this.options(this.search) : this.options || [];
        const resultObservable = observablize<Iterable<T>>(getOptions);

        resultObservable.subscribe((result = []) => {
            let options = Array.from(result)
                .filter(thing => !!thing)
                .map(
                    thing => new SpsOptionListOption(
                        thing,
                        typeof thing === "function"
                        ? {
                            textKey: "label",
                            captionKey: "caption"
                        }
                        : {
                            textKey: this.textKey,
                            captionKey: this.captionKey
                        }
                    )
                );

            if (this.nullOption) {
                options.unshift(this._nullOptionOption);
            }

            this._optionList = Object.freeze(options);

            if (isEmpty(this.search)) {
                delete this._searchReplacementPattern;
            } else {
                this._searchReplacementPattern = new RegExp(this.search, "ig");
                options = options.filter(option => (new RegExp(this.search, "i")).test(option.text));
            }

            this._optionList = Object.freeze(options);

            this.changeDetector.markForCheck();
        });
    }
}
